import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createLeague } from '../services/api';
import getErrorMessage from '../utils/errorHandler';
import { HIDE_MONEY } from '../utils/platform';
import '../styles/app/forms.css';

function CreatePool() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    entry_fee: 0,
    payout_first: 60,
    payout_second: 30,
    payout_third: 10,
    fight_inclusion: 'all', // 'main', 'main_prelims', or 'all'
    method_required_for: 'main', // 'main', 'main_prelims', or 'all'
    championship_multiplier: 1.5
  });
  const [chargeEntryFee, setChargeEntryFee] = useState(false);
  const [acceptedPolicy, setAcceptedPolicy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const value = e.target.type === 'number' ? parseFloat(e.target.value) : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const payoutSum = formData.payout_first + formData.payout_second + formData.payout_third;
    if (payoutSum !== 100) {
      setError(`Payout percentages must add up to 100% (currently ${payoutSum}%)`);
      return;
    }

    if (!acceptedPolicy) {
      setError('Please confirm this pool is for a private group.');
      return;
    }

    setLoading(true);

    try {
      // Native: the money fields are hidden, so formData still holds the web
      // form's untouched defaults (entry_fee 0, 60/30/10). They're sent
      // explicitly because the server defaults a missing entry fee to 10.
      const response = await createLeague(formData);
      navigate(`/pool/${response.data.id}/settings?setup=true`);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const fightOptions = [
    { value: 'main', label: 'Main card only' },
    { value: 'main_prelims', label: 'Main + Prelims' },
    { value: 'all', label: 'All fights' },
  ];

  return (
    <div className="container pnp-form-page">
      <header className="pnp-page-head pnp-rise">
        <p className="pnp-eyebrow">New pool</p>
        <h1 className="pnp-title">Create a <span className="pnp-punch">Pool</span></h1>
        <p className="pnp-page-sub">
          Start your own pick'em pool and invite friends to join
        </p>
      </header>

      {error && (
        <div className="message message-error" role="alert">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="pnp-form-stack">
        <section className="pnp-form-section pnp-rise" style={{ '--i': 1 }}>
          <div className="pnp-form-section-head">
            <span className="pnp-form-step">01</span>
            <div>
              <h2 className="pnp-form-section-title">The basics</h2>
              <p className="pnp-form-section-sub">Name it something your crew will recognize.</p>
            </div>
          </div>

          <div className="pnp-form-field">
            <label className="form-label" htmlFor="cp-name">Pool Name</label>
            <input
              id="cp-name"
              type="text"
              name="name"
              className="form-input"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., Office Fight Club"
              required
            />
          </div>

          <div className="pnp-form-field">
            <label className="form-label" htmlFor="cp-desc">Description (optional)</label>
            <textarea
              id="cp-desc"
              name="description"
              className="form-input"
              value={formData.description}
              onChange={handleChange}
              placeholder="Tell members what your pool is about"
              rows="3"
            />
          </div>
        </section>

        <section className="pnp-form-section pnp-rise" style={{ '--i': 2 }}>
          <div className="pnp-form-section-head">
            <span className="pnp-form-step">02</span>
            <div>
              <h2 className="pnp-form-section-title">Scoring rules</h2>
              <p className="pnp-form-section-sub">Which fights count and how title fights score.</p>
            </div>
          </div>

          <div className="pnp-form-field">
            <span className="form-label" id="cp-fights">Fight Selection</span>
            <div className="pnp-segmented pnp-form-seg" role="radiogroup" aria-labelledby="cp-fights">
              {fightOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={formData.fight_inclusion === opt.value}
                  className={formData.fight_inclusion === opt.value ? 'active' : ''}
                  onClick={() => setFormData({ ...formData, fight_inclusion: opt.value })}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <p className="pnp-form-help">
              Decide which fights members must pick for each event.
            </p>
          </div>

          <div className="pnp-form-field">
            <span className="form-label" id="cp-method">Method of Victory Required For</span>
            <div className="pnp-segmented pnp-form-seg" role="radiogroup" aria-labelledby="cp-method">
              {fightOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={formData.method_required_for === opt.value}
                  className={formData.method_required_for === opt.value ? 'active' : ''}
                  onClick={() => setFormData({ ...formData, method_required_for: opt.value })}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <p className="pnp-form-help">
              Choose which fights require a method of victory prediction (bonus points).
            </p>
          </div>

          <div className="pnp-form-field">
            <label className="form-label" htmlFor="cp-mult">Championship Fight Multiplier</label>
            <div className="pnp-form-adorn has-suf pnp-form-narrow">
              <input
                id="cp-mult"
                type="number"
                name="championship_multiplier"
                className="form-input"
                value={formData.championship_multiplier}
                onChange={handleChange}
                min="1"
                step="0.1"
                required
              />
              <span className="pnp-form-adorn-suf" aria-hidden="true">&times;</span>
            </div>
            <p className="pnp-form-help">
              Title fights earn base points multiplied by this value.
            </p>
          </div>
        </section>

        {!HIDE_MONEY && (
        <section className="pnp-form-section pnp-rise" style={{ '--i': 3 }}>
          <div className="pnp-form-section-head">
            <span className="pnp-form-step">03</span>
            <div>
              <h2 className="pnp-form-section-title">Entry fee &amp; payouts</h2>
              <p className="pnp-form-section-sub">Optional. Leave it off for a free pool.</p>
            </div>
          </div>

          <div className="pnp-form-field">
            <span className="form-label">Entry Fee</span>
            <label className="pnp-form-toggle">
              <div
                className={`pnp-form-switch${chargeEntryFee ? ' is-on' : ''}`}
                onClick={() => {
                  const newVal = !chargeEntryFee;
                  setChargeEntryFee(newVal);
                  if (!newVal) setFormData({ ...formData, entry_fee: 0 });
                  else if (formData.entry_fee === 0) setFormData({ ...formData, entry_fee: 10 });
                }}
              >
                <div className="pnp-form-switch-knob" />
              </div>
              <span>
                Charge entry fee per card
              </span>
            </label>
            {chargeEntryFee && (
              <div className="pnp-form-reveal">
                <div className="pnp-form-adorn has-pre pnp-form-narrow">
                  <span className="pnp-form-adorn-pre" aria-hidden="true">$</span>
                  <input
                    type="number"
                    name="entry_fee"
                    className="form-input"
                    value={formData.entry_fee}
                    onChange={handleChange}
                    min="1"
                    step="1"
                    required
                    placeholder="10"
                    aria-label="Entry fee"
                  />
                </div>
                <p className="pnp-form-help">
                  Members pay this amount for each UFC event.
                </p>
              </div>
            )}
          </div>

          <div className="pnp-form-field">
            <span className="form-label">Payout Structure</span>
            <div className="pnp-form-payouts">
              <div className="pnp-form-payout">
                <label className="pnp-form-place pnp-form-place-1" htmlFor="cp-p1">
                  1st Place
                </label>
                <div className="pnp-form-adorn has-suf">
                  <input
                    id="cp-p1"
                    type="number"
                    name="payout_first"
                    className="form-input"
                    value={formData.payout_first}
                    onChange={handleChange}
                    min="0"
                    max="100"
                    required
                  />
                  <span className="pnp-form-adorn-suf">%</span>
                </div>
              </div>
              <div className="pnp-form-payout">
                <label className="pnp-form-place pnp-form-place-2" htmlFor="cp-p2">
                  2nd Place
                </label>
                <div className="pnp-form-adorn has-suf">
                  <input
                    id="cp-p2"
                    type="number"
                    name="payout_second"
                    className="form-input"
                    value={formData.payout_second}
                    onChange={handleChange}
                    min="0"
                    max="100"
                    required
                  />
                  <span className="pnp-form-adorn-suf">%</span>
                </div>
              </div>
              <div className="pnp-form-payout">
                <label className="pnp-form-place pnp-form-place-3" htmlFor="cp-p3">
                  3rd Place
                </label>
                <div className="pnp-form-adorn has-suf">
                  <input
                    id="cp-p3"
                    type="number"
                    name="payout_third"
                    className="form-input"
                    value={formData.payout_third}
                    onChange={handleChange}
                    min="0"
                    max="100"
                    required
                  />
                  <span className="pnp-form-adorn-suf">%</span>
                </div>
              </div>
            </div>
            <p className="pnp-form-help">
              Must add up to 100%
            </p>
          </div>
        </section>
        )}

        <div className="pnp-form-confirm pnp-rise" style={{ '--i': 4 }}>
          <div className="pnp-form-confirm-row">
            <label className="pnp-form-check">
              <input
                type="checkbox"
                checked={acceptedPolicy}
                onChange={(e) => setAcceptedPolicy(e.target.checked)}
              />
              <span className="pnp-form-check-label">
                This pool is for a private group and not public wagering.
              </span>
            </label>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => setShowTerms(true)}
            >
              View Terms
            </button>
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-lg btn-block"
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create Pool'}
          </button>
        </div>
      </form>

      {showTerms && (
        <div className="modal-backdrop" onClick={() => setShowTerms(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="cp-terms-title">
            <p className="pnp-eyebrow">Read before you start</p>
            <h2 className="pnp-form-modal-title" id="cp-terms-title">Terms &amp; Disclaimer</h2>
            <p className="pnp-form-modal-body">
              Pools are intended for private groups of friends or coworkers. Pick 'N Pray is for entertainment only and is
              not affiliated with UFC. You are responsible for complying with local laws and regulations.
            </p>
            <button className="btn btn-primary btn-block" onClick={() => setShowTerms(false)}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default CreatePool;
